import { createContext, useContext, useEffect, useRef, useState } from 'react';
import type { EquipmentUITemplateEntity } from '@liveline/ll_node_common';

/**
 * LT-3320 — "show me this sensor" on Signal Data (SCADA2).
 *
 * A caller elsewhere in the app (the cell-alerts footer) selects the line, then navigates to
 * SIGNAL_DATA_PATH with a SignalFocusRequest as router state. signalDataContainer consumes it
 * once that line's template has loaded: it opens the equipment card holding the tag and
 * publishes the tag through SignalFocusContext, and the row (or flow item) carrying it
 * scrolls into view and highlights. Router state, not a query string, so a reload or a
 * shared link never re-triggers it.
 */
export const SIGNAL_DATA_PATH = '/live_data/scada2';

export interface SignalFocusRequest {
    focusTagId: number;
    /** The line the tag belongs to — the request waits for THAT line's template. */
    focusCellId: number;
    /** The sensor's display name, for the "not on this page" notice. */
    focusLabel?: string;
}

export const isSignalFocusRequest = (state: unknown): state is SignalFocusRequest => {
    const s = state as SignalFocusRequest | null;
    return (
        !!s &&
        typeof s.focusTagId === 'number' &&
        Number.isFinite(s.focusTagId) &&
        typeof s.focusCellId === 'number' &&
        Number.isFinite(s.focusCellId)
    );
};

/**
 * The equipment card that shows `tagId` on SCADA2, or null when the page would not show it.
 * Mirrors what the page renders: present equipment only, its FIRST view only (the error-code
 * view toggle is disabled), present columns / flow items only.
 */
export const findEquipmentForTag = (
    templates: EquipmentUITemplateEntity[] | undefined,
    tagId: number,
): number | null => {
    for (const equipment of templates ?? []) {
        if (!equipment?.isPresent) continue;
        const sections = (equipment.template?.views?.[0]?.sections ?? []) as any[];
        const shown = sections.some((section) => {
            if (section?.presentation === 'row_column') {
                return (section?.rows ?? []).some((row: any) =>
                    (row?.columns ?? []).some(
                        (column: any) =>
                            column?.valueItem?.isPresent && column?.kafkaConnection?.equipmentDataTagId === tagId,
                    ),
                );
            }
            if (section?.presentation === 'flow') {
                return (section?.flowItems ?? []).some(
                    (item: any) => item?.valueItem?.isPresent && item?.kafkaConnection?.equipmentDataTagId === tagId,
                );
            }
            return false;
        });
        if (shown) return equipment.equipmentId;
    }
    return null;
};

export interface SignalFocusContextValue {
    /** The tag to bring into view, or null when nothing is being focused. */
    tagId: number | null;
    /** Distinguishes two requests for the same tag, so a second click scrolls again. */
    nonce: number;
    /** Called by the element that took the focus, so the container stops waiting for one. */
    claim: (nonce: number) => void;
}

// Outside the provider nothing is ever focused, so rows elsewhere behave exactly as before.
export const SignalFocusContext = createContext<SignalFocusContextValue>({
    tagId: null,
    nonce: 0,
    claim: () => undefined,
});

const HIGHLIGHT_MS = 6000;

/**
 * For a SCADA2 row or flow item: when the focus names one of `tagIds`, scroll the element
 * into view and highlight it for a few seconds. Attach `ref` to the element to scroll.
 */
export const useSignalFocus = <T extends HTMLElement = HTMLDivElement>(tagIds: Array<number | null | undefined>) => {
    const { tagId, nonce, claim } = useContext(SignalFocusContext);
    const ref = useRef<T | null>(null);
    const [highlighted, setHighlighted] = useState(false);
    const matches = tagId != null && tagIds.includes(tagId);

    useEffect(() => {
        const el = ref.current;
        // Unrendered (a hidden row) is not a claim — the container's notice then explains why.
        if (!matches || !el) return;
        claim(nonce);
        setHighlighted(true);
        // After the card the container just expanded has laid out. Deliberately not cancelled
        // on cleanup: the claim re-renders this with matches=false before the frame fires.
        requestAnimationFrame(() => el.scrollIntoView?.({ block: 'center', behavior: 'smooth' }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [matches, nonce]);

    // Its own effect, so clearing the focus (the claim) cannot cancel the fade-out timer.
    useEffect(() => {
        if (!highlighted) return;
        const timer = setTimeout(() => setHighlighted(false), HIGHLIGHT_MS);
        return () => clearTimeout(timer);
    }, [highlighted, nonce]);

    return { ref, highlighted };
};
